import { eachMonthOfInterval, format, getMonth, getYear, startOfMonth, subMonths } from 'date-fns';
import { SummaryCostDetails, SummaryCostType } from '../../hooks';
import { Icons } from '../../stories/atoms';
import { Colors } from '../../theme';

export type ProfitLossFinancialDateConfig = {
  monthMap: Record<string, string>;
  availableYears: string[];
  currentFinancialYear: string;
};

const getFinancialYearStart = (date: Date) => (getMonth(date) === 11 ? getYear(date) : getYear(date) - 1);

const toFinancialYearLabel = (startYear: number) => `${startYear} - ${startYear + 1}`;

export const getFinancialYearEndYear = (financialYearLabel: string): string =>
  financialYearLabel.split(' - ')[1] ?? financialYearLabel;

export const getFinancialYearDisplayLabel = (financialYearLabel: string): string =>
  `FY ${getFinancialYearEndYear(financialYearLabel)}`;

export const getProfitLossFinancialDateConfig = (): ProfitLossFinancialDateConfig => {
  const endMonth = subMonths(startOfMonth(new Date()), 1);
  const currentFYStart = getFinancialYearStart(endMonth);
  const currentFYLabel = toFinancialYearLabel(currentFYStart);
  const firstFYStart = currentFYStart - 1;
  const startMonth = new Date(firstFYStart, 11, 1);
  const months = eachMonthOfInterval({ start: startMonth, end: endMonth });

  const monthMapData: Record<string, string> = {};
  const financialYearSet = new Set<string>();

  months.forEach((date) => {
    monthMapData[format(date, 'yyyyMM')] = format(date, 'MMMM yyyy');
    financialYearSet.add(toFinancialYearLabel(getFinancialYearStart(date)));
  });

  const availableYearsList = Array.from(financialYearSet).sort(
    (a, b) => Number.parseInt(b.split(' - ')[0]) - Number.parseInt(a.split(' - ')[0]),
  );

  return {
    monthMap: monthMapData,
    availableYears: availableYearsList,
    currentFinancialYear: currentFYLabel,
  };
};

export const generateCardTitle = (departmentID: string, costName: string, isParent: boolean): string => {
  return `${!isParent && departmentID !== '' ? `${departmentID} - ` : ''} ${costName}`;
};

const isValueBelowThreshold = (threshold: number, value: string) => {
  const numberWithoutCommas = value.replace(/,/g, '');
  return Number.parseInt(numberWithoutCommas) < threshold;
};

export const getColor = (label: string, value: string, costName: string) => {
  if (label === 'indexVsLY' || label === 'indexVsBudget') {
    if (costName === 'salesExcVat' || costName === 'salesIncVat' || costName === 'surplus1') {
      return isValueBelowThreshold(100, value) ? 'validationGreen' : 'red';
    }
    return isValueBelowThreshold(100, value) ? 'red' : 'validationGreen';
  }

  return 'primary';
};

export const getTrendIcon = (costName: string, textColor: Colors) => {
  if (costName === 'salesExcVat' || costName === 'salesIncVat' || costName === 'surplus1') {
    return textColor === 'validationGreen' ? Icons.TRENDDOWN : Icons.TRENDUP;
  }
  return textColor === 'validationGreen' ? Icons.TRENDUP : Icons.TRENDDOWN;
};

const calculateIndexValue = (originalValue: number, comparisonValue: number) => {
  if (comparisonValue === 0 || originalValue === 0) {
    return 0;
  }
  let indexValue = 0;
  const percentageChange = ((originalValue - comparisonValue) / comparisonValue) * 100;
  if (comparisonValue < 0) {
    indexValue = percentageChange < 0 ? 100 - percentageChange : 100 + percentageChange;
  } else {
    indexValue = percentageChange < 0 ? 100 - Math.abs(percentageChange) : 100 + percentageChange;
  }

  return Number.parseFloat(indexValue.toFixed());
};

const calculateSobValue = (value: number | null, compareValue: number | null) => {
  if (!value || value === 0 || !compareValue || compareValue === 0) {
    return 0;
  }
  return ((value / compareValue) * 100).toFixed(1);
};

const isSummaryCostType = (costName: string): costName is SummaryCostType => {
  const summaryCostTypes: SummaryCostType[] = ['salesExcVat', 'salesIncVat'];
  return summaryCostTypes.includes(costName as SummaryCostType);
};

export const profitLossPropertyToValueMap = (
  costName: string,
  details: SummaryCostDetails,
  compareCostDetails: SummaryCostDetails,
) => {
  const { actual, lastYear, budget } = details;
  const { actual: salesExclActual, lastYear: salesExclLastYear, budget: salesExclBudget } = compareCostDetails;

  const isSummary = isSummaryCostType(costName);

  const indexVsLY = calculateIndexValue(actual, lastYear ?? 0);
  const indexVsBudget = calculateIndexValue(actual, budget ?? 0);
  const sob = isSummary ? 100 : calculateSobValue(actual, salesExclActual);
  const sobLY = isSummary ? 100 : calculateSobValue(lastYear, salesExclLastYear);
  const sobBudget = isSummary ? 100 : calculateSobValue(budget, salesExclBudget);

  return [
    { key: 'actuals', value: actual },
    { key: 'indexVsLY', value: indexVsLY },
    { key: 'indexVsBudget', value: indexVsBudget },
    { key: 'sob', value: `${sob}%` },
    { key: 'sobLY', value: `${sobLY}%` },
    { key: 'sobBudget', value: `${sobBudget}%` },
  ];
};
